import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useNotifications = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the caller's notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdNotifications());
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching notifications:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark a single notification as read
  const markNotificationRead = useCallback(async (notificationId) => {
    try {
      const response = await apiService.post(apiUrl.aipdNotificationStatus(), {
        notificationId,
      });
      return response.data;
    } catch (err) {
      console.error("Error marking notification read:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Mark all notifications as read
  const markAllNotificationsRead = useCallback(async () => {
    try {
      const response = await apiService.post(apiUrl.aipdReadAllNotifications(), {});
      return response.data;
    } catch (err) {
      console.error("Error marking all notifications read:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Reply to a comment/topic (used by the notification "Reply" action)
  const createComment = useCallback(async (payload) => {
    try {
      const response = await apiService.post(apiUrl.aipdCreateCommentV2(), payload);
      return response.data;
    } catch (err) {
      console.error("Error creating comment:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Switch the caller's default persona/user-group (used by the "Switch & View" dialog)
  const switchDefaultUserGroup = useCallback(async (email, userGroupId) => {
    try {
      const response = await apiService.post(apiUrl.aipdUpdateDefaultUserGroup(), {
        email,
        userGroupId,
      });
      return response.data;
    } catch (err) {
      console.error("Error switching persona:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    loading,
    error,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    createComment,
    switchDefaultUserGroup,
    clearError,
  };
};

export default useNotifications;
